// The 6-digit code Supabase emails to confirm a new account or reset a password
// (docs/plans/signup.md). The box takes digits only, and asking for another code
// is held back for a minute.

export const CODE_LENGTH = 6
/** Seconds before "Send a new code" unlocks. */
export const RESEND_WAIT = 60

/** What was typed or pasted, as the code's digits ("482 916" → "482916"). */
export function cleanCode(text: string): string {
  return text.replace(/\D/g, '').slice(0, CODE_LENGTH)
}

/** Seconds as the countdown shows them ("0:42"). */
export function clock(seconds: number): string {
  const s = Math.max(0, Math.ceil(seconds))
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

const codeOf = (error: unknown): string =>
  typeof error === 'object' && error !== null && 'code' in error
    ? String((error as { code: unknown }).code)
    : ''

/** The account exists but its email was never confirmed (sign-in is refused). */
export function isUnconfirmed(error: unknown): boolean {
  return codeOf(error) === 'email_not_confirmed'
}

/** Another email was asked for before the server allows one. */
export function isTooSoon(error: unknown): boolean {
  return ['over_email_send_rate_limit', 'over_request_rate_limit'].includes(codeOf(error))
}

/**
 * Why a code was refused, in words. Supabase gives the same answer for a wrong
 * code and for one that has run out, so the sentence covers both.
 */
export function codeErrorText(error: unknown): string {
  const code = codeOf(error)
  const message = error instanceof Error ? error.message : String(error)
  if (code === 'otp_expired' || /expired|invalid/i.test(message))
    return 'That code is not right or has run out. Check the email, or send a new one.'
  if (isTooSoon(error)) return 'Wait a minute before asking for another code.'
  return message
}
